import moment from 'moment'
import { resetDateTime } from 'shared/lib/helpers'
import { ZENDESK_TICKET_URL } from 'shared/constants'

import {
  FETCH_PRICING_OVERRIDES_ERROR,
  FETCH_PRICING_OVERRIDES_REQUEST,
  FETCH_PRICING_OVERRIDES_SUCCESS,
  FETCH_WARRANTY_CLAIMS_ERROR,
  FETCH_WARRANTY_CLAIMS_REQUEST,
  FETCH_WARRANTY_CLAIMS_SUCCESS,
  SET_WARRANTY_CLAIM_DETAILS,
  FETCH_WARRANTY_CLAIM_BY_ID,
  UPLOAD_ATTACHMENTS_BY_ID_SUCCESS,
  ORDER_RESPONSE,
  RETAILERS_FETCH_FAIL,
  RETAILERS_FETCH_START,
  RETAILERS_FETCH_SUCCESS,
  UPDATE_ORDER_SOURCE,
  UPDATE_PRICING_OVERRIDES_ERROR,
  UPDATE_PRICING_OVERRIDES_SUCCESS,
  UPDATE_SEARCH_STRING,
  UPDATE_SELECTED_RETAILER,
  WARRANTIES_OVER_TIME_RESPONSE,
} from './actionTypes'

const initialState = {
  isLoading: true,
  orders: [],
  pageCount: 0,
  currentPage: 0,
  limit: 10,
  offset: 0,
  source: undefined,
  activeAggregation: 'month',
  activeStartDate: resetDateTime(moment().subtract({ months: 6 })),
  activeEndDate: moment(),
  retailers: null,
  selectedRetailer: null,
  searchString: '',
  overrides: [],
  totalClaims: 0,
  claims: [],
  claimDetails: {},
}

export default (state = initialState, action) => {
  switch (action.type) {
    case WARRANTIES_OVER_TIME_RESPONSE: {
      const { warrantiesOverTime } = action.dashboard
      const { activeAggregation, activeEndDate } = action

      let { activeStartDate } = action

      activeStartDate = resetDateTime(activeStartDate)

      return {
        ...state,
        warrantiesOverTime: warrantiesOverTime.warranties,
        totalSold: warrantiesOverTime.total_sold,
        grossCustomerCost: warrantiesOverTime.gross_customer_cost,
        grossInsurerRevenue: warrantiesOverTime.gross_insurer_revenue,
        grossRetailerRevenue: warrantiesOverTime.gross_retailer_revenue,
        grossMBRevenue: warrantiesOverTime.gross_mb_revenue,
        cancelledWarrantyOrders: warrantiesOverTime.cancelled_warranties,
        totalOrders: warrantiesOverTime.total_orders,
        activeAggregation,
        activeStartDate,
        activeEndDate,
      }
    }

    case ORDER_RESPONSE:
      return Object.assign({}, state, {
        orders: action.orders.orders,
        pageCount: Math.ceil(action.orders.total / 10),
      })

    case RETAILERS_FETCH_START:
      return {
        ...state,
      }

    case RETAILERS_FETCH_SUCCESS:
      return {
        ...state,
        retailers: action.data.data,
      }

    case RETAILERS_FETCH_FAIL:
      return {
        ...state,
      }

    case UPDATE_SELECTED_RETAILER:
      return {
        ...state,
        selectedRetailer: action.retailer,
      }

    case UPDATE_SEARCH_STRING: {
      return {
        ...state,
        searchString: action.searchString,
      }
    }

    case UPDATE_ORDER_SOURCE: {
      return {
        ...state,
        source: action.source,
      }
    }

    case FETCH_PRICING_OVERRIDES_REQUEST:
      return {
        ...state,
        isLoading: true,
      }

    case FETCH_PRICING_OVERRIDES_SUCCESS: {
      const {
        data: { data },
      } = action

      return {
        ...state,
        isLoading: false,
        overrides: data,
      }
    }

    case FETCH_PRICING_OVERRIDES_ERROR: {
      const { error } = action

      return {
        ...state,
        isLoading: false,
        error,
      }
    }

    case UPDATE_PRICING_OVERRIDES_SUCCESS: {
      const {
        data: { data },
      } = action

      const newOverrides = state.overrides.map((item) =>
        item.id === data.id ? data : item
      )

      return {
        ...state,
        isLoading: false,
        overrides: newOverrides,
      }
    }

    case UPDATE_PRICING_OVERRIDES_ERROR: {
      const { error } = action

      return {
        ...state,
        isLoading: false,
        error,
      }
    }

    case FETCH_WARRANTY_CLAIMS_REQUEST:
      return {
        ...state,
        isLoading: true,
      }

    case FETCH_WARRANTY_CLAIMS_SUCCESS: {
      const { data } = action

      return {
        ...state,
        isLoading: false,
        claims: data,
        totalClaims: data.total_count,
      }
    }

    case FETCH_WARRANTY_CLAIMS_ERROR: {
      const { error } = action

      return {
        ...state,
        isLoading: false,
        error,
      }
    }

    case SET_WARRANTY_CLAIM_DETAILS: {
      const { data } = action

      return {
        ...state,
        claimDetails: data,
      }
    }

    case UPLOAD_ATTACHMENTS_BY_ID_SUCCESS: {
      // `data` is the documents one upload saved (a 201 array or a 207
      // `documents` list), in posted order; core returns only that
      // request's documents. They go in front of what the claim already
      // has, newest first like the claim GET's supplemental_documents.
      const { data } = action
      const incoming = Array.isArray(data)
        ? data
        : (data && Array.isArray(data.documents) && data.documents) || []
      const existing =
        (state.claimDetails && state.claimDetails.attachments) || []
      const known = new Set(existing.map((doc) => doc.uuid))

      return {
        ...state,
        claimDetails: {
          ...state.claimDetails,
          attachments: incoming
            .filter((doc) => !doc.uuid || !known.has(doc.uuid))
            .reverse()
            .concat(existing),
        },
      }
    }

    case FETCH_WARRANTY_CLAIM_BY_ID: {
      const {
        data: { data },
      } = action

      const ONE_DAY_MS = 1000 * 60 * 60 * 24
      const daysSince = (claimDate) => {
        const today = new Date()
        // This is close but not exactly what the backend calculates
        // and is displayed in the list view.
        return Math.ceil((today - claimDate) / ONE_DAY_MS)
      }

      const notesData = data.notes.map(
        (note) =>
          `${note.admin_email} (${new Date(
            note.created_on
          ).toLocaleDateString()}): ${note.note_text}`
      )

      // Normalize response
      const claim = {
        attachments: data.supplemental_documents,
        claimDate: data.claim_date,
        claimId: data.id,
        createdAt: data.created_at,
        daysInPipeline: daysSince(new Date(data.created_at)),
        daysSinceIncident: daysSince(new Date(data.claim_date)),
        planStartDate: data.plan_start_date,
        planEndDate: data.plan_end_date,
        customer: data.customer,
        customerName: data.customer_first_name,
        description: data.description,
        history: data.history || [], // Required
        insurerName: data.insurer_name,
        clipName: data.clip_name,
        durationMonths: `${data.duration_months} months`,
        maxPayout: data.max_payout || null,
        claimMaxPayout: data.claim_max_payout ?? null,
        orderDetailId: data.order_detail_id,
        externalOrderId: data.external_order_id,
        retailerName: data.retailer_name,
        policyId: data.policy_number, // Required
        productName: data.product_name,
        productPrice: data.product_price,
        retailPrice: data.product_price,
        claimedProduct: data.claimed_product.toString(),
        // Pre-existing claims (filed before the picker landed) won't include
        // this key; default to an empty array so downstream consumers can
        // always iterate safely.
        serviceLineItems: data.service_line_items || [],
        submittedBy: data.submitted_by || null,
        statusName: data.status_name,
        statusId: data.status_id,
        transcript: data.transcript,
        warrantyId: data.warranty_issued_id,
        planStatus: data.warranty_plan_status || null,
        meta: data.meta,
        zendeskId: data.zendesk_id,
        notes: notesData,
        zendeskLink:
          data.zendesk_id === null
            ? null
            : `${ZENDESK_TICKET_URL}${data.zendesk_id}`,
      }

      return {
        ...state,
        claimDetails: { ...claim },
      }
    }

    default:
      return state
  }
}
